<script lang="ts">
  import type { Config } from '@pin/schemas/common';
  import type {
    ClientToServerEvent as OverlayClientToServerEvents,
    ServerToClientEvents as OverlayServerToClientEvents,
  } from '@pin/schemas/overlay';
  import type {
    ChatMessageAdmin,
    ClientToServerEvents,
    ServerToClientEvents,
  } from '@pin/schemas/socket';
  import { distinctUntilChanged, firstValueFrom } from 'rxjs';
  import type { Socket } from 'socket.io-client';
  import { onMount } from 'svelte';
  import { fetchEventHistory } from '../api/api';
  import { pollSubject$ } from '../api/polls.api';
  import { stickyMessageSubject$ } from '../api/stickyMessage.api';
  import ChatFeed from '../components/ChatFeed/ChatFeed.svelte';
  import ControlSection from '../components/ControlFeed/ControlSection.svelte';
  import ArrowLeft from '../components/Icons/ArrowLeft.svelte';
  import ArrowRight from '../components/Icons/ArrowRight.svelte';
  import OverlayFeed from '../components/OverlayFeed/OverlayFeed.svelte';
  import PaneDivider from '../components/PaneDivider.svelte';
  import { idToken, loginWithMicrosoft, logout, user } from '../firebase/auth';
  import { addToast } from '../stores/toastStore';
  import { connectAdminSocket } from '../utils/adminSocket';
  import {
    type ChatState,
    initialChatState,
    reconstructStream,
  } from '../utils/history';
  import { localStorageWritable } from '../utils/localstoreWritable';

  let config = $state<Config | null>(null);
  let chatState = $state<ChatState>(initialChatState);
  let feedPaused = $state(false);
  let userFeedUser = $state<ChatMessageAdmin['user'] | null>(null);
  let controlSectionMinimized = $state(false);
  let overlaySectionMinimized = $state(false);

  // Widths of the side panes in percent of the window; the chat feed in the
  // middle takes the rest
  const MIN_SIDE = 15;
  const MIN_MIDDLE = 20;
  const DEFAULT_SIDE = 100 / 3;
  const paneWidths = localStorageWritable('pane-widths', {
    left: DEFAULT_SIDE,
    right: DEFAULT_SIDE,
  });
  let resizing = $state(false);
  let controlPaneHidden = $derived(
    controlSectionMinimized && userFeedUser == null
  );
  let gridColumns = $derived(
    [
      controlPaneHidden ? 'auto' : `${$paneWidths.left}%`,
      'minmax(0, 1fr)',
      overlaySectionMinimized ? 'auto' : `${$paneWidths.right}%`,
    ].join(' ')
  );
  // The button sits in the overlay pane's corner; keep it clear of the
  // pane's minimize button however narrow the pane is dragged
  let loginButtonMaxWidth = $derived(
    overlaySectionMinimized ? undefined : `calc(${$paneWidths.right}vw - 4rem)`
  );

  let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  let overlaySocket: Socket<
    OverlayServerToClientEvents,
    OverlayClientToServerEvents
  > | null = $state(null);

  const createSocket = () => {
    socket?.disconnect();
    overlaySocket?.disconnect();
    socket = connectAdminSocket(import.meta.env.VITE_ADMIN_API_URL, {
      getIdToken: () => firstValueFrom(idToken),
    });
    overlaySocket = connectAdminSocket(
      `${import.meta.env.VITE_ADMIN_API_URL}/overlay`,
      { getIdToken: () => firstValueFrom(idToken) }
    );

    socket.on('ADMIN', (msg) => {
      switch (msg.type) {
        case 'CHAT_MESSAGE':
        case 'MUTED_CHAT_MESSAGE': {
          reconstructStream(chatState, msg, { trim: !feedPaused });
          break;
        }
        case 'MUTE_USER':
        case 'UNMUTE_USER':
        case 'MODERATION_SEVERITY': {
          reconstructStream(chatState, msg);
          break;
        }
        case 'POLLS_CHANGED': {
          pollSubject$.next();
          break;
        }
        case 'STICKY_MESSAGES_CHANGED': {
          stickyMessageSubject$.next();
          break;
        }
      }
    });
    // A change made while the socket was down was announced to nobody
    socket.on('connect', () => {
      pollSubject$.next();
      stickyMessageSubject$.next();
    });
    socket.on('SHARED', (msg) => {
      switch (msg.type) {
        case 'CONFIG': {
          config = msg.config;
          break;
        }
        case 'REDACT_MESSAGE':
        case 'BAN_USER':
        case 'UNBAN_USER': {
          reconstructStream(chatState, msg);
        }
      }
    });
  };

  const loadHistory = async (take = 100) => {
    try {
      const eventHistory = await fetchEventHistory({
        startId: chatState.earliestMessageId,
        take,
      });
      if (eventHistory.length === 0) {
        addToast({ message: 'No more messages to fetch' });
      }
      for (const event of eventHistory) {
        reconstructStream(chatState, event, { prepend: true });
      }
    } catch (e) {
      addToast({
        message: `Couldn't fetch history: ${String(e)}`,
        type: 'error',
      });
    }
  };
  onMount(() => {
    const authStateSub = user
      .pipe(distinctUntilChanged((prev, curr) => prev?.uid === curr?.uid))
      .subscribe((currentUser) => {
        config = null;
        chatState = initialChatState;
        if (currentUser == null) {
          socket?.disconnect();
          overlaySocket?.disconnect();
          socket = null;
          overlaySocket = null;
          return;
        }
        console.log('Creating socket due to session change');
        createSocket();
        loadHistory();
      });

    return () => {
      authStateSub.unsubscribe();
      socket?.disconnect();
      overlaySocket?.disconnect();
    };
  });
</script>

{#if $user == null}
  <button
    class="login-button"
    class:overlay-section-minimized={overlaySectionMinimized}
    onclick={loginWithMicrosoft}
  >
    Not logged in
  </button>
{:else}
  <button
    class="login-button"
    class:overlay-section-minimized={overlaySectionMinimized}
    style:max-width={loginButtonMaxWidth}
    title="Logged in as {$user.displayName}"
    onclick={logout}
  >
    Logged in as {$user.displayName}
  </button>
{/if}
{#if $user != null}
  <main class:resizing style:grid-template-columns={gridColumns}>
    {#if !controlPaneHidden}
      <PaneDivider
        label="Resize info pane"
        position={$paneWidths.left}
        min={MIN_SIDE}
        max={100 - $paneWidths.right - MIN_MIDDLE}
        defaultPosition={DEFAULT_SIDE}
        onDragChange={(dragging) => (resizing = dragging)}
        onMove={(left) => ($paneWidths = { ...$paneWidths, left })}
      />
    {/if}
    {#if !overlaySectionMinimized}
      <PaneDivider
        label="Resize overlay feed"
        position={100 - $paneWidths.right}
        min={$paneWidths.left + MIN_MIDDLE}
        max={100 - MIN_SIDE}
        defaultPosition={100 - DEFAULT_SIDE}
        onDragChange={(dragging) => (resizing = dragging)}
        onMove={(position) =>
          ($paneWidths = { ...$paneWidths, right: 100 - position })}
      />
    {/if}
    <div class="feed-wrapper" class:minimized={controlPaneHidden}>
      <button
        class="minimize-button"
        onclick={() => (controlSectionMinimized = !controlSectionMinimized)}
        title={controlSectionMinimized ? 'Show info pane' : 'Hide info pane'}
      >
        <ArrowLeft />
      </button>
      {#if !controlSectionMinimized || userFeedUser != null}
        <ControlSection
          {config}
          chatMessages={chatState.messages}
          {overlaySocket}
          bind:userFeedUser
        />
      {/if}
    </div>
    <div class="feed-wrapper">
      <ChatFeed
        onLoadHistory={loadHistory}
        chatMessages={chatState.messages}
        bind:userFeedUser
        bind:paused={feedPaused}
        {overlaySocket}
      />
    </div>
    <div
      class="feed-wrapper feed-wrapper--overlay"
      class:minimized={overlaySectionMinimized}
    >
      <button
        class="minimize-button minimize-button--overlay"
        onclick={() => (overlaySectionMinimized = !overlaySectionMinimized)}
        title={overlaySectionMinimized
          ? 'Show overlay feed'
          : 'Hide overlay feed'}
      >
        <ArrowRight />
      </button>
      {#if !overlaySectionMinimized}
        <OverlayFeed
          {overlaySocket}
          onUserClick={(user) =>
            // The overlay only carries id and name; take birth year and
            // viaUserId from the chat when the user is still in it
            (userFeedUser =
              chatState.messages.find((m) => m.user.id === user.id)?.user ??
              user)}
        />
      {/if}
    </div>
  </main>
{/if}

<style>
  .login-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 1rem;
    padding: 0.5rem 1rem;
    cursor: pointer;
    background: transparent;
    border: 1px solid var(--color-zinc-700);
    border-radius: 0.5rem;
    color: var(--color-zinc-200);
    transition: background 0.15s;
    z-index: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .login-button.overlay-section-minimized {
    right: 4rem;
  }
  .login-button:hover {
    background: var(--color-zinc-800);
  }

  main {
    position: relative;
    display: grid;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
  }
  main.resizing {
    cursor: col-resize;
    user-select: none;
  }

  .feed-wrapper {
    position: relative;
    border: 1px solid rgb(83, 83, 83);
    overflow-y: auto;
  }
  .feed-wrapper.minimized {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 0.5rem;
  }

  .minimize-button {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 1;
    background-color: var(--color-zinc-700);
    border: 1px solid var(--color-zinc-600);
    border-radius: 0.5rem;
    padding: 0.25rem;
    cursor: pointer;
    color: var(--color-zinc-200);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }
  .minimize-button:hover {
    background-color: var(--color-zinc-600);
  }
  .minimized .minimize-button {
    position: static;
    transform: rotate(180deg);
  }
  .feed-wrapper--overlay {
    padding-top: 2.5rem;
  }
  .minimize-button--overlay {
    right: auto;
    left: 0.5rem;
  }
</style>
